/**
 * The "Welcome back" checklist: prompts a returning member to fill in any
 * program-level mandatory fields they missed during onboarding, and to
 * confirm their member attributes. Rendered on the onboarding card.
 *
 * Not dismissible: the user must complete every row and save to continue.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  FormControl,
  Avatar,
  DialogActions,
  DialogContent,
  InputLabel
} from '@mui/material';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import { toast } from 'react-toastify';
import { API } from '../../../api';
import { getUserDetails } from '../../../utils/orgName';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  userBioUpdate,
  userInfoUpdate
} from '../../../redux/slices/user-profile/userProfieSlice';
import useResponsiveScreenSwitch from '../../../utils/useResponsiveScreenSwitch';
import { handleImageCompression, imageSizeLimit, sortedCountries } from './common';
import { FieldMeta } from '../utils/missingMandatoryFields';
import OnboardingDialog from '../onboarding/OnboardingDialog';
import OnboardingShell from '../onboarding/OnboardingShell';
import { onboardingColors as C } from '../onboarding/onboardingTheme';

interface Props {
  open: boolean;
  fields: FieldMeta[];
  /**
   * Called after a successful save so the parent can refresh user data and
   * re-evaluate whether anything is still missing.
   */
  onSaved: () => void;
  /**
   * Open the existing dedicated modal for a complex field. The parent owns the
   * modal stack to avoid double-mounting Dialogs.
   */
  onOpenExternal: (action: NonNullable<FieldMeta['externalAction']>) => void;
  /**
   * Called when the user opts to skip after a save error. The parent should
   * persist a session-level dismiss so the modal won't re-open in this session.
   */
  onSkip: () => void;
  /**
   * When true, show the "Complete later" escape hatch. Reserved for internal
   * Dosen team users so they can navigate the app while testing other orgs.
   */
  allowSkip: boolean;
}

type Values = Record<string, any>;

const MissingMandatoryFieldsModal: React.FC<Props> = ({
  open,
  fields,
  onSaved,
  onOpenExternal,
  onSkip,
  allowSkip
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.userProfile.data);
  const { orgId, userId } = getUserDetails();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [headshotPreview, setHeadshotPreview] = useState<string>('');
  const { forMobile } = useResponsiveScreenSwitch();

  // Persist in-progress inline values across unmounts. The trigger remounts the
  // modal whenever it calls `fetchUserProfile()` (e.g. after the user closes an
  // external auxiliary modal like Work History / Learning Objectives) — without
  // this, selections like "Department" get wiped while the user is filling
  // other fields.
  const STORAGE_KEY = `dosen_missing_fields_values_${userId || 'anon'}`;
  const readStored = (): Partial<Values> => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Partial<Values>) : {};
    } catch {
      return {};
    }
  };

  const initialValues = useMemo<Values>(() => {
    const v: Values = {};
    fields.forEach((f) => {
      if (f.kind !== 'externalModal') {
        v[f.name] = f.getInitialValue ? f.getInitialValue(user) : '';
      }
    });
    return v;
  }, [fields, user]);

  const [values, setValues] = useState<Values>(() => ({
    ...initialValues,
    ...readStored()
  }));
  const [saving, setSaving] = useState(false);

  // When the set of fields changes (e.g. user just completed an external field),
  // re-seed any fields the user hasn't already started filling in this session.
  React.useEffect(() => {
    setValues((prev) => {
      const stored = readStored();
      const next: Values = { ...initialValues, ...stored };
      // Preserve any user-edited values already in `prev` that aren't yet persisted
      // (covers the very first change before the persistence effect runs).
      Object.keys(prev).forEach((k) => {
        if (next[k] === undefined || next[k] === '' || next[k] === null) {
          next[k] = prev[k];
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // Persist on every change. Strip File instances (not JSON-serializable).
  React.useEffect(() => {
    try {
      const serializable: Values = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v instanceof File || v instanceof Blob) return;
        serializable[k] = v;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
    } catch {
      // ignore quota / serialization errors
    }
  }, [values, STORAGE_KEY]);

  const inlineFields = fields.filter((f) => f.kind !== 'externalModal');
  const externalFields = fields.filter((f) => f.kind === 'externalModal');

  const isInlineFieldValid = (f: FieldMeta) => {
    const v = values[f.name];
    if (f.kind === 'multiselect') return Array.isArray(v) && v.length > 0;
    if (f.kind === 'image') {
      // Either an existing headshot URL string, or a freshly-picked File
      return (typeof v === 'string' && v.trim() !== '') || v instanceof File;
    }
    return typeof v === 'string' ? v.trim() !== '' : v != null;
  };

  const allInlineValid = inlineFields.every(isInlineFieldValid);
  const allExternalFilled = externalFields.every((f) => f.isFilled(user));
  const canSave = allInlineValid && allExternalFilled;

  const setValue = (name: string, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const buildFormData = (): FormData => {
    const fd = new FormData();
    // Preserve existing identity fields so the BE doesn't blank them out
    const pd: any = user?.personal_details ?? {};
    fd.append('firstName', pd.firstName ?? '');
    fd.append('lastName', pd.lastName ?? '');
    fd.append('timezone', pd.timezone ?? '');
    fd.append('category', pd.category ?? '');
    // Re-send existing values for fields not part of this modal but read by the BE
    if (pd.phoneNumber) fd.append('phoneNumber', pd.phoneNumber);
    if (pd.phonePrefix) fd.append('phonePrefix', pd.phonePrefix);

    inlineFields.forEach((f) => {
      const v = values[f.name];
      switch (f.name) {
        case 'bio':
          // bio has a dedicated endpoint (POST /updateBio) — handled separately in handleSave.
          break;
        case 'headshot':
          // Only include a freshly-picked File. The BE turns formData.image into headshot.
          if (v instanceof File) fd.append('image', v);
          break;
        case 'phoneNumber':
          fd.set('phoneNumber', v ?? '');
          break;
        case 'dob':
          fd.append('dob', v ?? '');
          break;
        case 'gender':
          fd.append('gender', v ?? '');
          break;
        case 'ethnicity':
          fd.append('ethnicity', v ?? '');
          break;
        case 'firstGenerationStudent':
          fd.append('firstGenStudent', v ?? '');
          break;
        case 'country':
          fd.append('country', v ?? '');
          break;
        case 'videoIntro':
          fd.append('videoIntro', v ?? '');
          break;
        case 'companyName':
          fd.append('companyName', v ?? '');
          break;
        case 'companyInfo':
          fd.append('companyInfo', v ?? '');
          break;
        case 'disabilityType':
          (Array.isArray(v) ? v : []).forEach((item) => fd.append('disability', item));
          break;
        case 'hobbiesInterests':
          (Array.isArray(v) ? v : []).forEach((item) => fd.append('hobbies', item));
          break;
        case 'department':
          fd.append('departments', JSON.stringify(Array.isArray(v) ? v : []));
          break;
        default:
          break;
      }
    });
    return fd;
  };

  const handleSave = async () => {
    if (!canSave) return;
    // Every row was completed through its own editor (nothing inline to
    // persist): just let the parent re-check.
    if (inlineFields.length === 0) {
      onSaved();
      return;
    }
    setSaving(true);
    try {
      // 1. Bio has a dedicated endpoint — updateUserProfile silently ignores formData.bio.
      const bioField = inlineFields.find((f) => f.name === 'bio');
      if (bioField) {
        const bioText: string = values.bio ?? '';
        const bioResp = await API.updateBio({ data: { bio: bioText }, orgId });
        const savedBio = bioResp?.data?.bioData ?? bioText;
        dispatch(userBioUpdate(savedBio));
      }

      // 2. Everything else goes through updateUserProfile (incl. headshot via formData.image)
      const fd = buildFormData();
      const response = await API.updateUserProfile({ data: fd, orgId });
      const updated = response?.data?.data ?? response?.data;
      if (updated) {
        dispatch(userInfoUpdate(updated));
      }
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
      toast.success('Profile updated');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Your profile could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderInlineField = (f: FieldMeta) => {
    const v = values[f.name];
    switch (f.kind) {
      case 'text':
      case 'tel':
      case 'url':
        return (
          <TextField
            fullWidth
            type={f.kind === 'tel' ? 'tel' : f.kind === 'url' ? 'url' : 'text'}
            value={v ?? ''}
            onChange={(e) => setValue(f.name, e.target.value)}
            placeholder={f.helper}
          />
        );
      case 'textarea':
        return (
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={v ?? ''}
            onChange={(e) => setValue(f.name, e.target.value)}
            placeholder={f.helper}
          />
        );
      case 'image': {
        const previewSrc =
          headshotPreview ||
          (typeof v === 'string' && v ? v : '') ||
          (user?.personal_details?.headshot ?? '');
        const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (file.size > imageSizeLimit().limitValue) {
            toast.error('Only png, jpeg, svg and gif files of less than 10MB can be uploaded');
            return;
          }
          const ext = file.name.split('.').pop()?.toLowerCase();
          const allowed = ['png', 'jpeg', 'jpg', 'svg', 'gif'];
          if (!ext || !allowed.includes(ext)) {
            toast.error('Only jpeg, png, svg and gif files can be uploaded');
            return;
          }
          let photo: File | Blob = file;
          if (['png', 'jpeg', 'jpg'].includes(ext)) {
            try {
              const compressed = await handleImageCompression(file);
              if (compressed) photo = compressed;
            } catch {
              // Fall back to the uncompressed file
            }
          }
          setValue(f.name, photo);
          setHeadshotPreview(URL.createObjectURL(photo));
        };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={previewSrc || undefined}
              sx={{ width: 64, height: 64, bgcolor: C.surface, border: `1px solid ${C.border}` }}
            >
              {!previewSrc && <CameraAltOutlinedIcon sx={{ color: C.inkSecondary }} />}
            </Avatar>
            <Button
              variant="outlined"
              size="small"
              sx={{ minHeight: 36, height: 36, px: 2 }}
              onClick={() => fileInputRef.current?.click()}
            >
              {previewSrc ? 'Change Photo' : 'Upload Photo'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/svg+xml, image/gif"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Typography sx={{ color: C.inkTertiary, fontSize: 12 }}>jpg or png, max 10MB</Typography>
          </Box>
        );
      }
      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            value={v ?? ''}
            onChange={(e) => setValue(f.name, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'select': {
        const options =
          f.name === 'country'
            ? sortedCountries.map((c: any) => ({ label: c.label, value: c.label }))
            : f.options ?? [];
        return (
          <FormControl fullWidth>
            <Select
              value={v ?? ''}
              onChange={(e) => setValue(f.name, e.target.value)}
              displayEmpty
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="" disabled>
                Select...
              </MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      case 'multiselect': {
        const selected: string[] = Array.isArray(v) ? v : [];
        // Department uses orgDepartments from the user profile — array of {_id, name},
        // value stored is the _id list. Other multiselects use static {label,value}[].
        if (f.name === 'department') {
          const deptOptions: { _id: string; name: string }[] = Array.isArray(user?.orgDepartments)
            ? (user?.orgDepartments as { _id: string; name: string }[])
            : [];
          const selectedDepts = deptOptions.filter((d) => selected.includes(d._id));
          return (
            <Autocomplete
              multiple
              options={deptOptions}
              value={selectedDepts}
              onChange={(_e, next) => setValue(f.name, next.map((d) => d._id))}
              getOptionLabel={(opt: any) => opt?.name ?? ''}
              isOptionEqualToValue={(opt: any, val: any) => opt._id === val._id}
              noOptionsText="No departments available"
              renderTags={(value: readonly any[], getTagProps) =>
                value.map((opt, index) => {
                  const tagProps = getTagProps({ index });
                  return (
                    <Chip label={opt.name} size="small" {...tagProps} />
                  );
                })
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Select departments" />
              )}
            />
          );
        }
        const options = (f.options ?? []).map((o) => o.value);
        return (
          <Autocomplete
            multiple
            freeSolo={!!f.freeSolo}
            options={options}
            value={selected}
            onChange={(_e, next) => setValue(f.name, next)}
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                return (
                  <Chip label={option} size="small" {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField {...params} placeholder={f.helper || 'Select or type'} />
            )}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <OnboardingDialog
      open={open}
      onClose={() => {}}
      fullScreen={forMobile}
      data-testid="welcome-back-dialog"
      aria-labelledby="welcome-back-heading"
    >
      <OnboardingShell
        required
        heading={<span id="welcome-back-heading">Welcome back</span>}
        hint="Your program requires a few more details. Complete them to continue."
      />
      <DialogContent>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            border: `1px solid ${C.accent}`,
            bgcolor: C.accentTint,
            color: C.accent,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}
        >
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.accent }} />
          Required to continue
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {externalFields.map((f) => {
            const filled = f.isFilled(user);
            return (
              <Box
                key={f.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: filled ? 'rgba(47,180,131,0.5)' : C.border,
                  bgcolor: C.surface
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: C.ink, fontSize: 14, fontWeight: 600 }}>{f.label}</Typography>
                  <Typography sx={{ color: filled ? C.success : C.inkTertiary, fontSize: 12, mt: 0.25 }}>
                    {filled ? 'Completed' : 'Not yet completed'}
                  </Typography>
                </Box>
                <Button
                  variant={filled ? 'outlined' : 'contained'}
                  size="small"
                  sx={{ minHeight: 36, height: 36, px: 2, flexShrink: 0 }}
                  onClick={() => onOpenExternal(f.externalAction!)}
                >
                  {filled ? 'Edit' : 'Complete'}
                </Button>
              </Box>
            );
          })}

          {inlineFields.map((f) => (
            <Box key={f.name}>
              <InputLabel>{f.label}</InputLabel>
              {renderInlineField(f)}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <Button variant="contained" fullWidth onClick={handleSave} disabled={saving || !canSave}>
          {saving ? 'Saving...' : 'Save and Continue'}
        </Button>
        {allowSkip && (
          <Button
            variant="text"
            fullWidth
            onClick={onSkip}
            sx={{ minHeight: 32, height: 32, fontSize: 12, fontWeight: 500, color: C.inkTertiary }}
          >
            Complete later, you will be asked again next session
          </Button>
        )}
      </DialogActions>
    </OnboardingDialog>
  );
};

export default MissingMandatoryFieldsModal;
